// src/lib/weldingReserves.js
// #746 - CHANGING A RESERVE (gate 746, 746 D29-D31; R1-R4), Session 79.22b.6.4b.
// The arithmetic the two reserve screens share, pure so weldingReserves746.test.js
// can hold it to the copy table and the interaction specification (section 7,
// F2 and F3). The words are weldingWords.js's; the database steps are
// set_weld_reserve() and set_weld_reserves() (20261011_welding_reserve_746.sql),
// called through welding.js.

import { STEPPER_MAX } from './qtyStepper';

// A reserve is a whole number of frames, 0 to 9,999 (746 D27). '' or anything
// else is not a number yet.
export const RESERVE_MAX = STEPPER_MAX;
export function reserveOf(value) {
  const s = String(value ?? '').trim();
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  return n <= RESERVE_MAX ? n : null;
}

// The percent box (F3): whole numbers from -100 to 1,000, typed. Anything else
// is not a percent yet. `typedPercent` keeps the box to what can become one, so
// a keystroke that would leave the range is simply not taken.
export const PERCENT_MIN = -100;
export const PERCENT_MAX = 1000;
export function percentOf(value) {
  const s = String(value ?? '').trim();
  if (!/^-?\d+$/.test(s)) return null;
  const n = Number(s);
  return n >= PERCENT_MIN && n <= PERCENT_MAX ? n : null;
}
export function typedPercent(previous, next) {
  const s = String(next ?? '').trim();
  if (s === '' || s === '-') return s;
  return percentOf(s) == null ? String(previous ?? '') : String(Number(s));
}

// Apply (F3): every box becomes its STARTING number x (1 + percent / 100),
// rounded to the nearest whole frame and held between 0 and 9,999 - replacing
// any number typed by hand. A size at 0 stays at 0.
export function applyPercent(start, percent) {
  const base = Math.max(Number(start) || 0, 0);
  const p = Number(percent) || 0;
  const n = Math.round(base * (1 + p / 100));
  return Math.min(Math.max(n, 0), RESERVE_MAX);
}

// What still to weld becomes with a reserve of `reserve` (R2's preview, 746
// D33): everything counted with the new reserve in place of the old, less what
// is welded and waiting, never below 0.
export function stillToWeldWith(size, reserve) {
  const counted = (Number(size?.counted) || 0) - (Number(size?.reserve) || 0) + (Number(reserve) || 0);
  return Math.max(counted - Math.max(Number(size?.waitingShown) || 0, 0), 0);
}

// The every-size list (R3, R4): every size the branch welds, in name order -
// as Grok drew it, Frames before Stakes because that is the alphabet.
export function sizesByName(sizes) {
  return [...(Array.isArray(sizes) ? sizes : [])]
    .sort((a, b) => String(a.name).localeCompare(String(b.name)) || String(a.itemId).localeCompare(String(b.itemId)));
}

// Each size's starting number and the box's value, keyed by item id.
export function startRows(sizes) {
  const rows = {};
  for (const s of Array.isArray(sizes) ? sizes : []) {
    const start = Math.max(Number(s.reserve) || 0, 0);
    rows[String(s.itemId)] = { start, value: String(start) };
  }
  return rows;
}

// The list's state for Save: the changed sizes ({ itemId, reserve, was }, what
// set_weld_reserves() takes), and whether any box is empty or not a number. A
// box brought back to its starting number is no longer a change (F3).
export function reserveChanges(sizes, rows) {
  const changes = [];
  let incomplete = false;
  for (const s of Array.isArray(sizes) ? sizes : []) {
    const r = rows?.[String(s.itemId)];
    if (!r) continue;
    const n = reserveOf(r.value);
    if (n == null) { incomplete = true; continue; }
    if (n !== r.start) changes.push({ itemId: s.itemId, reserve: n, was: r.start });
  }
  return { changes, incomplete };
}

// Does this box show "was N"? Whenever its value is not its starting number.
export function rowChanged(row) {
  return !!row && String(row.value) !== String(row.start);
}
